// Canonical status configuration for quotes.
//
// Single source of truth for status labels, colors, and customer-vs-operator
// label divergence. Consumed by:
//   - Quotes.tsx (list page)
//   - QuoteDetail.tsx (detail page) — uses border field
//   - PortalQuotes.tsx (customer portal) — uses customerLabel for sent/viewed
//
// Adding a status: extend the QuoteStatus union below + the config map +
// the quotes_status_check constraint (migration). All three must agree.

export type QuoteStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'approved'
  | 'declined'
  | 'invoiced'
  | 'won'
  | 'expired'

export interface QuoteStatusConfig {
  label: string
  color: string
  bg: string
  border: string
  // Customer-facing label used by PortalQuotes when the operator-facing
  // label would leak workflow detail to the customer (e.g., 'sent' →
  // 'Awaiting approval'). Defaults to label when not set.
  customerLabel?: string
}

export const QUOTE_STATUS_CONFIG: Record<QuoteStatus, QuoteStatusConfig> = {
  draft:    { label: 'Draft',    color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)',  border: 'var(--border-default)' },
  sent:     { label: 'Sent',     color: 'var(--info)',         bg: 'var(--info-dim)',       border: 'var(--info-border)',     customerLabel: 'Awaiting approval' },
  viewed:   { label: 'Viewed',   color: 'var(--accent-text)',  bg: 'var(--accent-dim)',     border: 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', customerLabel: 'Awaiting approval' },
  approved: { label: 'Approved', color: 'var(--positive)',     bg: 'var(--positive-dim)',   border: 'var(--positive-border)' },
  declined: { label: 'Declined', color: 'var(--danger)',       bg: 'var(--danger-dim)',     border: 'var(--danger-border)' },
  invoiced: { label: 'Invoiced', color: 'var(--warning)',      bg: 'var(--warning-dim)',    border: 'var(--warning-border)' },
  won:      { label: 'Won',      color: 'var(--positive)',     bg: 'var(--positive-dim)',   border: 'var(--positive-border)' },
  expired:  { label: 'Expired',  color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)',       border: 'var(--border-subtle)' },
}

export function getQuoteStatusConfig(status: string | null | undefined): QuoteStatusConfig {
  return QUOTE_STATUS_CONFIG[(status as QuoteStatus) || 'draft'] ?? QUOTE_STATUS_CONFIG.draft
}

export function getCustomerLabel(status: string | null | undefined): string {
  const cfg = getQuoteStatusConfig(status)
  return cfg.customerLabel ?? cfg.label
}

// Status filter pill set used by Quotes.tsx list page.
// Excludes 'viewed' (no writer today) and 'expired' (reserved, no writer)
// for tidier filter UI; both still display via the config map when present
// on rows. Add 'won' so the conversion outcome is filterable.
export const QUOTE_STATUS_FILTER_OPTIONS: Array<'all' | QuoteStatus> = [
  'all', 'draft', 'sent', 'approved', 'declined', 'invoiced', 'won',
]
